/**
 * DRAPEAU DE RÉCENCE — détection des textes modifiés après la période de
 * connaissance du modèle.
 *
 * Constat mesuré (23/08/2026) : la vérification contrôlait l'EXISTENCE d'une
 * référence, jamais la FRAÎCHEUR de son contenu. L'article 787 B du CGI existe
 * et est en vigueur, mais sa version en vigueur date du 21/02/2026 : la valeur
 * citée de mémoire par le modèle (« engagement individuel : 4 ans ») était
 * périmée sans que rien ne s'allume.
 *
 * Donnée d'appui : la base officielle renvoie bien un champ
 * « Date début vigueur: JJ/MM/AAAA » (relevé brut du 23/08/2026), propagé
 * jusqu'ici sous `officialVersionDate` au format ISO. Lorsqu'il est absent,
 * AUCUN drapeau n'est levé et AUCUNE date n'est inventée.
 */
import type { VerifiedReference } from "@/lib/api/verify-references";

/**
 * Date de fin présumée de la connaissance du modèle. Configurable sans
 * redéploiement lourd via `VITE_MODEL_KNOWLEDGE_CUTOFF` (format ISO
 * `AAAA-MM-JJ`). Valeur par défaut volontairement PRUDENTE : mieux vaut un
 * signalement de trop qu'une valeur périmée présentée comme sûre.
 */
export const MODEL_KNOWLEDGE_CUTOFF: string =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_MODEL_KNOWLEDGE_CUTOFF) ||
  "2025-06-01";

function isIsoDate(value: string | undefined): value is string {
  return !!value && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * VRAI si la version officielle du texte est POSTÉRIEURE au cutoff : la
 * mémoire du modèle est alors présumée non fiable sur ce texte.
 * Faux si la date est absente, illisible, ou antérieure/égale au cutoff.
 */
export function isRecentlyModified(
  ref: Pick<VerifiedReference, "kind" | "status" | "officialVersionDate">,
  cutoff: string = MODEL_KNOWLEDGE_CUTOFF,
): boolean {
  if (ref.kind !== "article") return false;
  if (ref.status !== "confirmed" && ref.status !== "abrogated") return false;
  if (!isIsoDate(ref.officialVersionDate) || !isIsoDate(cutoff)) return false;
  return ref.officialVersionDate > cutoff;
}

/** « 2026-02-21 » → « 21/02/2026 ». */
export function formatFrenchDate(iso: string): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return undefined;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Référence minimale manipulée par le drapeau de récence. */
type RecencyRef = Pick<
  VerifiedReference,
  "kind" | "status" | "officialVersionDate" | "reconciledVersionDate"
>;

/**
 * VRAI si la réponse a été RÉCONCILIÉE sur le texte réellement en vigueur :
 * la version lue doit être exactement celle signalée par la source officielle.
 * Une réconciliation sur une autre version ne lève pas le drapeau.
 */
export function isReconciledOnOfficialText(ref: RecencyRef): boolean {
  return (
    isIsoDate(ref.officialVersionDate) &&
    ref.reconciledVersionDate === ref.officialVersionDate
  );
}

/**
 * VRAI lorsque le drapeau reste un AVERTISSEMENT : texte récent, non encore
 * confronté à son contenu officiel.
 */
export function isPendingRecentModification(
  ref: RecencyRef,
  cutoff: string = MODEL_KNOWLEDGE_CUTOFF,
): boolean {
  return isRecentlyModified(ref, cutoff) && !isReconciledOnOfficialText(ref);
}

/**
 * Libellé du badge :
 *  - « modifié le JJ/MM/AAAA — contenu à recouper » tant que le texte réel
 *    n'a pas été lu ;
 *  - « contenu vérifié sur le texte en vigueur du JJ/MM/AAAA » une fois la
 *    réconciliation faite sur ce texte.
 * `undefined` lorsqu'aucun drapeau n'est levé.
 */
export function recentModificationLabel(
  ref: RecencyRef,
  cutoff: string = MODEL_KNOWLEDGE_CUTOFF,
): string | undefined {
  if (!isRecentlyModified(ref, cutoff)) return undefined;
  const date = formatFrenchDate(ref.officialVersionDate!);
  if (!date) return undefined;
  return isReconciledOnOfficialText(ref)
    ? `contenu vérifié sur le texte en vigueur du ${date}`
    : `modifié le ${date} — contenu à recouper`;
}

/** VRAI si AU MOINS une référence de la réponse est récemment modifiée. */
export function hasRecentlyModifiedReference(
  refs: readonly RecencyRef[] | undefined,
  cutoff: string = MODEL_KNOWLEDGE_CUTOFF,
): boolean {
  return (refs ?? []).some((r) => isRecentlyModified(r, cutoff));
}

/** VRAI si AU MOINS un drapeau reste non levé (avertissement actif). */
export function hasPendingRecentModification(
  refs: readonly RecencyRef[] | undefined,
  cutoff: string = MODEL_KNOWLEDGE_CUTOFF,
): boolean {
  return (refs ?? []).some((r) => isPendingRecentModification(r, cutoff));
}

